import type { DevicesDevice, DevicesDeviceState } from '@smarthome/api';
import { useDebounce } from '@smarthome/ui';
import { useEffect, useRef, useState } from 'react';

import { byte255ToPercent, getDeviceOn, percentToByte255 } from '../../utils';

import { ColorPicker, type RGBColor, Slider, Toggle } from '@/components/ui';

export interface LightControlsProps {
  device: DevicesDevice;
  onUpdateState: (state: DevicesDeviceState) => void;
  disabled?: boolean;
}

export function LightControls({ device, onUpdateState, disabled }: LightControlsProps) {
  const capabilities = device.capabilities?.light;
  const lightState = device.state?.light;
  const isOn = getDeviceOn(device);
  const presets = (capabilities?.colorTemperaturePresets ?? [])
    .filter((preset) => preset.id)
    .map((preset) => ({
      id: preset.id ?? '',
      label: preset.label ?? preset.id ?? '',
    }));

  // Local state for brightness slider (prevents API spam while dragging)
  const rawBrightness = lightState?.brightness ?? 0;
  const [localBrightness, setLocalBrightness] = useState(() => byte255ToPercent(rawBrightness));
  const debouncedBrightness = useDebounce(localBrightness, 300);

  // Local state for color picker
  const rawColor = lightState?.color ?? null;
  const [localColor, setLocalColor] = useState<RGBColor | null>(() =>
    rawColor
      ? { red: rawColor.red ?? 255, green: rawColor.green ?? 255, blue: rawColor.blue ?? 255 }
      : null
  );
  const debouncedColor = useDebounce(localColor, 300);

  // Track if we initiated the change to avoid sync loops
  const isUserBrightnessChange = useRef(false);
  const lastSentBrightness = useRef(debouncedBrightness);
  const isUserColorChange = useRef(false);
  const lastSentColor = useRef(debouncedColor);

  // Sync local state with device state when it changes externally
  useEffect(() => {
    if (!isUserBrightnessChange.current) {
      setLocalBrightness(byte255ToPercent(rawBrightness));
    }
  }, [rawBrightness]);

  useEffect(() => {
    if (!isUserColorChange.current && rawColor) {
      setLocalColor({
        red: rawColor.red ?? 255,
        green: rawColor.green ?? 255,
        blue: rawColor.blue ?? 255,
      });
    }
  }, [rawColor]);

  // Send debounced brightness updates
  useEffect(() => {
    if (isUserBrightnessChange.current && debouncedBrightness !== lastSentBrightness.current) {
      lastSentBrightness.current = debouncedBrightness;
      onUpdateState({ light: { brightness: percentToByte255(debouncedBrightness) } });
      isUserBrightnessChange.current = false;
    }
  }, [debouncedBrightness, onUpdateState]);

  // Send debounced color updates
  useEffect(() => {
    if (isUserColorChange.current && debouncedColor && debouncedColor !== lastSentColor.current) {
      lastSentColor.current = debouncedColor;
      // Turn on the light if it's off when selecting a color (nice UX)
      if (!isOn) {
        onUpdateState({ light: { on: true, color: debouncedColor } });
      } else {
        onUpdateState({ light: { color: debouncedColor } });
      }
      isUserColorChange.current = false;
    }
  }, [debouncedColor, isOn, onUpdateState]);

  const handlePowerToggle = (checked: boolean) => {
    onUpdateState({ light: { on: checked } });
  };

  const handleBrightnessChange = (value: number) => {
    isUserBrightnessChange.current = true;
    setLocalBrightness(value);
  };

  const handleColorTempPreset = (preset: { id: string; label: string }) => {
    const payload = { colorTemperaturePresetId: preset.id };
    // Turn on the light if it's off when selecting a temperature (nice UX)
    if (!isOn) {
      onUpdateState({ light: { on: true, ...payload } });
    } else {
      onUpdateState({ light: payload });
    }
  };

  const handleColorChange = (color: RGBColor) => {
    isUserColorChange.current = true;
    setLocalColor(color);
  };

  // Check if a color temp preset is selected
  const isColorTempSelected = (preset: { id: string }): boolean => {
    return lightState?.colorTemperaturePresetId === preset.id;
  };

  return (
    <div className="space-y-1">
      {/* Power toggle row */}
      <div className="rounded-lg px-3 py-2.5 transition-colors hover:bg-gray-100 dark:hover:bg-white/[0.02]">
        <Toggle
          label="Power"
          layout="switch-right"
          checked={isOn ?? false}
          onChange={handlePowerToggle}
          disabled={disabled}
        />
      </div>

      {/* Brightness slider (only if supported) */}
      {capabilities?.brightness && (
        <>
          <div className="mx-3 h-px bg-gray-200 dark:bg-white/[0.04]" />
          <div className="px-3 py-2.5">
            <Slider
              label="Brightness"
              min={0}
              max={100}
              value={localBrightness}
              onChange={handleBrightnessChange}
              formatValue={(v) => `${v}%`}
              disabled={disabled}
            />
          </div>
        </>
      )}

      {/* Color picker (only if supported) */}
      {capabilities?.color && (
        <>
          <div className="mx-3 h-px bg-gray-200 dark:bg-white/[0.04]" />
          <div className="px-3 py-2.5">
            <ColorPicker
              label="Color"
              value={localColor}
              onChange={handleColorChange}
              disabled={disabled}
              size={140}
            />
          </div>
        </>
      )}

      {/* Color temperature presets (only if supported) */}
      {capabilities?.colorTemperature && presets.length > 0 && (
        <>
          <div className="mx-3 h-px bg-gray-200 dark:bg-white/[0.04]" />
          <div className="px-3 py-2.5">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-zinc-300">
              Color Temperature
            </label>
            <div className="flex flex-wrap gap-1.5">
              {presets.map((preset) => {
                const selected = isColorTempSelected(preset);
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => handleColorTempPreset(preset)}
                    disabled={disabled}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition-all ${
                      selected
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700'
                    } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
